import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  TrendingUp, Briefcase, FileText, Euro, Star, CheckCircle2,
  BarChart3, PieChart, Loader2, ArrowUpRight, ArrowDownRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AnalyticsPage = () => {
  const { user, isCompany } = useAuth();
  const [data, setData] = useState(null);
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [analyticsRes, badgesRes] = await Promise.all([
          axios.get(`${API}/analytics/dashboard`, { withCredentials: true }),
          axios.get(`${API}/users/${user?.id}/badges`)
        ]);
        setData(analyticsRes.data);
        setBadges(badgesRes.data || []);
      } catch (err) {
        console.error('Analytics fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    if (user?.id) fetchData();
  }, [user?.id]);

  const badgeIcons = {
    'award': '🏅',
    'trophy': '🏆',
    'crown': '👑',
    'star': '⭐',
    'shield': '🛡',
    'check-circle': '✓',
    'zap': '⚡'
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#1E3A8A]" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-[#4B5563]">Données non disponibles</p>
      </div>
    );
  }

  const ov = data.overview || {};

  // Simple bar chart using CSS
  const BarChart = ({ items, label }) => {
    const maxVal = Math.max(...items.map(i => i.count), 1);
    return (
      <div>
        <p className="text-sm font-medium text-[#374151] mb-3">{label}</p>
        <div className="flex items-end gap-2 h-32">
          {items.map((item, i) => (
            <div key={item.month || i} className="flex-1 flex flex-col items-center gap-1">
              <span className="text-xs font-medium text-[#374151]">{item.count}</span>
              <div
                className="w-full bg-[#1E3A8A] rounded-t-sm transition-all duration-500 min-h-[4px]"
                style={{ height: `${(item.count / maxVal) * 100}%` }}
              />
              <span className="text-xs text-[#6B7280]">{item.month}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] py-8" data-testid="analytics-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="font-heading font-bold text-3xl text-[#111827]" data-testid="analytics-title">
            Tableau de bord analytique
          </h1>
          <p className="text-[#4B5563] mt-1">
            {isCompany ? 'Performance de vos chantiers et recrutements' : 'Suivi de vos candidatures et missions'}
          </p>
        </div>

        {/* Trust Badges */}
        {badges.length > 0 && (
          <Card className="mb-6 border-[#F59E0B]/30" data-testid="trust-badges-card">
            <CardHeader>
              <CardTitle className="font-heading text-base">Badges de confiance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                {badges.map((badge) => (
                  <div
                    key={badge.id}
                    className="flex items-center gap-2 px-3 py-2 bg-[#F59E0B]/10 border border-[#F59E0B]/20 rounded-full"
                    data-testid={`badge-${badge.id}`}
                  >
                    <span className="text-lg">{badgeIcons[badge.icon] || '🏅'}</span>
                    <div>
                      <p className="text-sm font-medium text-[#111827]">{badge.name}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* KPI Cards */}
        {isCompany ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card data-testid="kpi-total-jobs">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <Briefcase className="w-5 h-5 text-[#1E3A8A]" />
                  <Badge className="bg-blue-50 text-blue-700 text-xs">{ov.open_jobs} ouverts</Badge>
                </div>
                <p className="text-2xl font-bold text-[#111827]">{ov.total_jobs}</p>
                <p className="text-sm text-[#4B5563]">Chantiers totaux</p>
              </CardContent>
            </Card>
            <Card data-testid="kpi-applications">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <FileText className="w-5 h-5 text-[#F59E0B]" />
                  <span className="text-xs font-medium text-emerald-600 flex items-center gap-1">
                    <ArrowUpRight className="w-3 h-3" />{ov.acceptance_rate}%
                  </span>
                </div>
                <p className="text-2xl font-bold text-[#111827]">{ov.total_applications}</p>
                <p className="text-sm text-[#4B5563]">Candidatures reçues</p>
              </CardContent>
            </Card>
            <Card data-testid="kpi-projects">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                </div>
                <p className="text-2xl font-bold text-[#111827]">{ov.completed_projects}/{ov.total_projects}</p>
                <p className="text-sm text-[#4B5563]">Projets terminés</p>
              </CardContent>
            </Card>
            <Card data-testid="kpi-spent">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <Euro className="w-5 h-5 text-purple-500" />
                </div>
                <p className="text-2xl font-bold text-[#111827]">{ov.total_spent?.toFixed(0) || 0}€</p>
                <p className="text-sm text-[#4B5563]">Total dépensé</p>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card data-testid="kpi-applications">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <FileText className="w-5 h-5 text-[#1E3A8A]" />
                  <Badge className="bg-yellow-50 text-yellow-700 text-xs">{ov.pending_applications} en attente</Badge>
                </div>
                <p className="text-2xl font-bold text-[#111827]">{ov.total_applications}</p>
                <p className="text-sm text-[#4B5563]">Candidatures</p>
              </CardContent>
            </Card>
            <Card data-testid="kpi-success-rate">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <TrendingUp className="w-5 h-5 text-emerald-500" />
                </div>
                <p className="text-2xl font-bold text-[#111827]">{ov.success_rate}%</p>
                <p className="text-sm text-[#4B5563]">Taux de succès</p>
              </CardContent>
            </Card>
            <Card data-testid="kpi-earned">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <Euro className="w-5 h-5 text-purple-500" />
                </div>
                <p className="text-2xl font-bold text-[#111827]">{ov.total_earned?.toFixed(0) || 0}€</p>
                <p className="text-sm text-[#4B5563]">Revenus totaux</p>
              </CardContent>
            </Card>
            <Card data-testid="kpi-rating">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <Star className="w-5 h-5 text-[#F59E0B]" />
                </div>
                <p className="text-2xl font-bold text-[#111827]">{ov.rating?.toFixed(1) || '0.0'}/5</p>
                <p className="text-sm text-[#4B5563]">{ov.review_count || 0} avis</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Charts */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Card data-testid="chart-monthly">
            <CardHeader>
              <CardTitle className="font-heading text-base flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-[#1E3A8A]" />
                {isCompany ? 'Chantiers publiés par mois' : 'Candidatures par mois'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <BarChart
                items={isCompany ? (data.monthly_jobs || []) : (data.monthly_applications || [])}
                label=""
              />
            </CardContent>
          </Card>

          {isCompany && data.trade_distribution && data.trade_distribution.length > 0 && (
            <Card data-testid="chart-trades">
              <CardHeader>
                <CardTitle className="font-heading text-base flex items-center gap-2">
                  <PieChart className="w-4 h-4 text-[#F59E0B]" />
                  Répartition par métier
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {data.trade_distribution.slice(0, 6).map((item, i) => {
                    const maxCount = data.trade_distribution[0]?.count || 1;
                    const colors = ['bg-[#1E3A8A]', 'bg-[#F59E0B]', 'bg-emerald-500', 'bg-purple-500', 'bg-blue-400', 'bg-orange-400'];
                    return (
                      <div key={item.trade}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-[#374151] capitalize">{item.trade}</span>
                          <span className="font-medium">{item.count}</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${colors[i % colors.length]}`}
                            style={{ width: `${(item.count / maxCount) * 100}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {!isCompany && (
            <Card data-testid="chart-stats">
              <CardHeader>
                <CardTitle className="font-heading text-base">Résumé</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-[#F9FAFB] rounded-lg">
                    <span className="text-sm text-[#4B5563]">Candidatures acceptées</span>
                    <span className="font-bold text-emerald-600">{ov.accepted_applications}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-[#F9FAFB] rounded-lg">
                    <span className="text-sm text-[#4B5563]">Projets terminés</span>
                    <span className="font-bold text-[#1E3A8A]">{ov.completed_projects}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-[#F9FAFB] rounded-lg">
                    <span className="text-sm text-[#4B5563]">Total des projets</span>
                    <span className="font-bold text-[#374151]">{ov.total_projects}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPage;
